<template lang="pug">
  .b-spec-split(data-testid="split-dialog")
    ._title {{ title }}
    ._item {{ item && item.name }} — {{ money(supplierPrice) }} × {{ quantity(qty) }}

    template(v-if="hasDiscount")
      ._line
        ._label Персональная скидка за единицу
        ._value
          span(data-testid="split-personal-discount-percent") {{ short(discountPercent) }}%
          |  (
          span(data-testid="split-personal-discount") {{ short(unitDiscount) }} ₽
          | )

      ._units
        button._unit(type="button" data-testid="split-unit-rub" :class="{ active: unit === 'rub' }" @click="setUnit('rub')") ₽
        button._unit(type="button" data-testid="split-unit-percent" :class="{ active: unit === 'percent' }" @click="setUnit('percent')") %

      ._line
        ._label Скидка заказчику
        BaseInput._input(inputTestId="split-customer-discount" v-model="customerInput" :error="error")
        ._unit-label {{ unit === 'rub' ? '₽ за единицу' : '% от персональной скидки' }}

      ._line
        ._label Вознаграждение дизайнера
        ._value(data-testid="split-designer-reward") {{ money(rewardPerUnit) }}
    ._no-discount(v-else data-testid="split-no-discount") Поставщик не предоставил персональную скидку — делить нечего.

    ._preview
      ._line
        ._label Цена для заказчика за единицу
        ._value(data-testid="split-preview-customer-price") {{ money(customerPrice) }}
      ._line
        ._label Сумма для заказчика
        ._value(data-testid="split-preview-customer-total") {{ money(round(customerPrice * qty, 2)) }}
      ._line
        ._label Доход дизайнера по позиции
        ._value(data-testid="split-preview-income") {{ money(designerIncome) }}

    ._line(v-if="mode === 'change'")
      ._label Причина изменения
      BaseInput._input(inputTestId="split-reason" v-model="reason" label="необязательно")

    ._buttons
      Btn(blue data-testid="split-submit" @click="submit") {{ mode === 'change' ? 'Сохранить' : 'Выбрать и отправить заказчику' }}
      Btn(grey data-testid="split-cancel" @click="$emit('finish', false)") Отмена
</template>

<script>
import BaseInput from '@/components/BaseInput.vue';
import { money, quantity, toNumber, round } from '@/utils/spec';

/**
 * Окно разделения персональной скидки (ТЗ 6.2, 7.1): персональная скидка за единицу — только
 * чтение, в процентах от цены поставщика и в рублях («20 % (2 000 ₽)», до сотых); дизайнер задаёт
 * скидку заказчику в процентах от персональной скидки (по умолчанию) или в рублях за единицу, вознаграждение и предпросмотр считаются на лету по формулам 7.1 — единственное место,
 * где клиенту разрешено считать самому (значение нужно до отправки).
 *
 * Вознаграждение дизайнера показывается вычисленным, а не вторым полем ввода: сумма полей всегда
 * равна персональной скидке, поэтому второе значение — производное от первого.
 *
 * Если поставщик не дал персональной скидки, блок разделения скрыт: остаются позиция,
 * предпросмотр и кнопка, скидка заказчику уходит нулём (доработка после сдачи).
 * Сгенерировано Claude.
 */
export default {
  name: 'SpecSplitPopupWrappable',
  components: { BaseInput },
  data() {
    return {
      mode: 'choose',
      item: null,
      offer: null,
      commissionPercent: 0,
      unit: 'percent',
      customerInput: '0',
      reason: '',
      error: '',
    };
  },
  computed: {
    supplierPrice() { return toNumber(this.offer?.supplierPrice) ?? 0; },
    unitDiscount() { return toNumber(this.offer?.unitDiscount) ?? 0; },
    /** Без персональной скидки блок разделения скрыт: скидка заказчику всегда 0. */
    hasDiscount() { return this.unitDiscount > 0; },
    title() {
      if (!this.hasDiscount) return 'Выбор отклика';
      return this.mode === 'change' ? 'Изменить разделение скидки' : 'Разделение скидки';
    },
    /** Персональная скидка в процентах от цены поставщика — для показа рядом с рублями. */
    discountPercent() {
      return this.supplierPrice ? (this.unitDiscount / this.supplierPrice) * 100 : 0;
    },
    /** Суммы позиции считаются от количества отклика — оно станет кол-вом факт позиции. */
    qty() { return toNumber(this.offer?.quantity ?? this.item?.quantity) ?? 0; },
    /** Скидка заказчику за единицу в рублях: процент — доля персональной скидки (ТЗ 7.1). */
    customerDiscount() {
      const value = toNumber(this.customerInput) ?? 0;
      const rub = this.unit === 'percent' ? (this.unitDiscount * value) / 100 : value;
      return round(Math.min(Math.max(rub, 0), this.unitDiscount), 2);
    },
    rewardPerUnit() { return round(this.unitDiscount - this.customerDiscount, 2); },
    customerPrice() { return round(this.supplierPrice - this.customerDiscount, 2); },
    designerIncome() {
      const reward = round(this.rewardPerUnit * this.qty, 2);
      const commission = round((this.customerPrice * this.qty * this.commissionPercent) / 100, 2);
      return round(reward + commission, 2);
    },
  },
  methods: {
    money,
    quantity,
    round,
    init(params = {}) {
      this.mode = params.mode ?? 'choose';
      this.item = params.item ?? null;
      this.offer = params.offer ?? null;
      this.commissionPercent = toNumber(params.commissionPercent) ?? 0;
      this.unit = 'percent';
      const current = toNumber(params.item?.customerDiscount) ?? 0;
      this.customerInput = this.mode === 'change' ? this.toPercentInput(current) : '0';
    },
    /** Скидка заказчику в рублях → поле в процентах от персональной скидки, до сотых. */
    toPercentInput(rub) {
      return this.unitDiscount ? String(round((rub / this.unitDiscount) * 100, 2)) : '0';
    },
    /** Число до сотых без лишних нулей: «20», «16,67», «2 000». */
    short(value) {
      return round(value, 2).toLocaleString('ru-RU', { maximumFractionDigits: 2 });
    },
    setUnit(unit) {
      if (this.unit === unit) return;
      // значение сохраняем по смыслу: рубли ↔ доля персональной скидки
      const rub = this.customerDiscount;
      this.unit = unit;
      if (unit === 'percent') {
        this.customerInput = this.toPercentInput(rub);
        return;
      }
      this.customerInput = String(rub);
    },
    submit() {
      const value = toNumber(this.customerInput);
      if (value === null || value < 0) {
        this.error = 'Укажите скидку заказчику';
        return;
      }
      this.error = '';
      this.$emit('finish', {
        customerDiscount: this.customerDiscount.toFixed(2),
        reason: this.reason.trim() || null,
      });
    },
  },
};
</script>

<style scoped lang="scss">
.spec-split {
  width: 520px;
  max-width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 12px;

  &__title {
    font-size: 24px;
    font-weight: 700;
  }

  &__item {
    color: var(--color-middle-gray);
    font-size: 13px;
  }

  &__line {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__label {
    flex-grow: 1;
    font-size: 13px;
  }

  &__value {
    font-weight: 700;
    white-space: nowrap;
  }

  &__input {
    width: 140px;
  }

  &__unit-label {
    font-size: 11px;
    color: var(--color-middle-gray);
    width: 150px;
  }

  &__units {
    display: flex;
    gap: 6px;
  }

  &__unit {
    width: 44px;
    padding: 5px 0;
    border: 1px solid var(--color-super-light);
    border-radius: 6px;
    background: var(--color-white);
    font: inherit;
    cursor: pointer;

    &.active {
      border-color: var(--color-accent);
      color: var(--color-accent);
    }
  }

  &__no-discount {
    font-size: 13px;
    color: var(--color-middle-gray);
  }

  &__preview {
    padding: 10px 12px;
    border-radius: 8px;
    background: #F8F9FC;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__buttons {
    display: flex;
    gap: 12px;
  }
}
</style>
